import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { LocalstorageService } from 'src/app/core/localstorage/localstorage.service';
import { TokenService } from 'src/app/core/token/token.service';
import { UsuariosService } from 'src/app/services/usuarios.service';
import { ThemeService } from 'src/app/shared/themes/theme.service';

@Injectable({ providedIn: 'root' })
export class TokenTimerService implements OnDestroy {
  private readonly tempoExpirarSubject = new BehaviorSubject<number | null>(null);
  public readonly tempoExpirar$ = this.tempoExpirarSubject.asObservable();

  private intervalVerificacao: any;
  private intervalTimerToken: any;
  private dataUltimoLogin: Date = this.getDataUltimoLogin();
  private iniciado = false;
  private logoutExecutado = false;
  private renovacaoEmAndamento$: Observable<void> | null = null;

  constructor(
    private usuariosService: UsuariosService,
    private tokenService: TokenService,
    private themeService: ThemeService,
    private storageService: LocalstorageService
  ) { }

  start(): void {
    if (!this.tokenService.getUsuario()) {
      this.stop();
      return;
    }

    if (!this.iniciado || this.tempoExpirarSubject.value === null) {
      this.dataUltimoLogin = this.getDataUltimoLogin();
    }

    this.iniciado = true;
    this.verificaTokenExpiracao();

    if (this.tempoExpirarSubject.value === null) {
      this.iniciaVerificacaoToken();
    }
  }

  renovaToken(): Observable<void> {
    if (this.renovacaoEmAndamento$) {
      return this.renovacaoEmAndamento$;
    }

    const usuario = this.tokenService.getUsuario();

    if (!usuario?.RefreshToken) {
      return throwError(() => new Error('RefreshToken nao encontrado'));
    }

    this.renovacaoEmAndamento$ = this.usuariosService
      .renovaToken({ refresh_token: usuario.RefreshToken })
      .pipe(
        tap((results) => {
          if (!results?.AuthenticationResult) {
            throw results;
          }

          this.dataUltimoLogin = new Date();
          usuario.ultimo_login = this.dataUltimoLogin;
          usuario.AccessToken = results.AuthenticationResult.AccessToken;
          usuario.RefreshToken = results.AuthenticationResult.RefreshToken;
          usuario.token = results;

          this.tokenService.setToken(usuario);
          this.tempoExpirarSubject.next(null);
          this.logoutExecutado = false;
          this.limparTimerToken();
          this.iniciaVerificacaoToken();
          this.themeService.emiteMessageInadimplencia('remove');
        }),
        map(() => undefined),
        finalize(() => {
          this.renovacaoEmAndamento$ = null;
        }),
        shareReplay(1)
      );

    return this.renovacaoEmAndamento$;
  }

  isTokenExpirando(): boolean {
    return this.tempoExpirarSubject.value !== null;
  }

  private verificaTokenExpiracao(): void {
    if (this.tempoExpirarSubject.value !== null) {
      return;
    }

    this.dataUltimoLogin = this.getDataUltimoLogin();
    const segundosLogado = this.getSegundosLogado();

    if (segundosLogado >= 35400) {
      const tempoRestante = 36000 - segundosLogado;

      if (tempoRestante <= 0) {
        this.logout(true);
        return;
      }

      this.tempoExpirarSubject.next(tempoRestante);
      this.comecaContagemExpiracao();
      this.limparVerificacaoToken();
      this.themeService.emiteMessageInadimplencia('add');
    }
  }

  private comecaContagemExpiracao(): void {
    this.limparTimerToken();
    this.intervalTimerToken = setInterval(() => {
      const tempoAtual = this.tempoExpirarSubject.value;
      const proximoTempo = tempoAtual !== null ? tempoAtual - 1 : null;

      this.tempoExpirarSubject.next(proximoTempo);

      if (proximoTempo === null || proximoTempo <= 0) {
        this.logout(true);
      }
    }, 1000);
  }

  private iniciaVerificacaoToken(): void {
    this.limparVerificacaoToken();
    this.intervalVerificacao = setInterval(() => {
      this.verificaTokenExpiracao();
    }, 60000);
  }

  private getDataUltimoLogin(): Date {
    const usuario = this.tokenService.getUsuario();
    const dataUltimoLogin = usuario?.ultimo_login
      ? new Date(usuario.ultimo_login)
      : new Date();

    return Number.isNaN(dataUltimoLogin.getTime())
      ? new Date()
      : dataUltimoLogin;
  }

  private getSegundosLogado(): number {
    return Math.floor((new Date().getTime() - this.dataUltimoLogin.getTime()) / 1000);
  }

  private logout(bypass?: boolean): void {
    if (bypass && !this.logoutExecutado) {
      this.logoutExecutado = true;
      this.stop();
      this.storageService.logoutWithLocalData();
    }
  }

  private stop(): void {
    this.iniciado = false;
    this.tempoExpirarSubject.next(null);
    this.limparTimerToken();
    this.limparVerificacaoToken();
  }

  private limparTimerToken(): void {
    if (this.intervalTimerToken) {
      clearInterval(this.intervalTimerToken);
      this.intervalTimerToken = null;
    }
  }

  private limparVerificacaoToken(): void {
    if (this.intervalVerificacao) {
      clearInterval(this.intervalVerificacao);
      this.intervalVerificacao = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
    this.renovacaoEmAndamento$ = null;
  }
}
